// Announcement Redux Slice
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Announcement,
  GetAnnouncementsPayload,
  UpsertAnnouncementPayload,
} from '../../types/announcement';
import * as announcementService from '../../services/announcementService';

interface AnnouncementState {
  announcements: Announcement[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  limit: number;
}

const initialState: AnnouncementState = {
  announcements: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  limit: 10,
};

// Async thunks
export const fetchAnnouncements = createAsyncThunk(
  'announcements/fetchAnnouncements',
  async (payload: GetAnnouncementsPayload, { rejectWithValue }) => {
    const response = await announcementService.getAnnouncements(payload);
    if (!response.success) {
      return rejectWithValue(response.message);
    }
    return response;
  }
);

export const createAnnouncement = createAsyncThunk(
  'announcements/createAnnouncement',
  async (announcement: Omit<Announcement, 'id'>, { rejectWithValue }) => {
    const response = await announcementService.createAnnouncement(announcement);
    if (!response.success) {
      return rejectWithValue(response.message);
    }
    return response;
  }
);

export const updateAnnouncement = createAsyncThunk(
  'announcements/updateAnnouncement',
  async (
    { id, updates }: { id: string; updates: Partial<Announcement> },
    { rejectWithValue }
  ) => {
    const response = await announcementService.updateAnnouncement(id, updates);
    if (!response.success) {
      return rejectWithValue(response.message);
    }
    return { id, updates, response };
  }
);

export const deleteAnnouncement = createAsyncThunk(
  'announcements/deleteAnnouncement',
  async (id: string, { rejectWithValue }) => {
    const response = await announcementService.deleteAnnouncement(id);
    if (!response.success) {
      return rejectWithValue(response.message);
    }
    return id;
  }
);

export const toggleAnnouncementStatus = createAsyncThunk(
  'announcements/toggleStatus',
  async (
    { id, is_active }: { id: string; is_active: boolean },
    { rejectWithValue }
  ) => {
    const response = await announcementService.toggleAnnouncementStatus(
      id,
      is_active
    );
    if (!response.success) {
      return rejectWithValue(response.message);
    }
    return { id, is_active };
  }
);

const announcementSlice = createSlice({
  name: 'announcements',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch announcements
    builder
      .addCase(fetchAnnouncements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.loading = false;
        state.announcements = action.payload.data || [];
        state.totalCount = action.payload.total_count || 0;
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create announcement
    builder
      .addCase(createAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnnouncement.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update announcement
    builder
      .addCase(updateAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        const { id, updates } = action.payload;
        const index = state.announcements.findIndex((a) => a.id === id);
        if (index !== -1) {
          state.announcements[index] = {
            ...state.announcements[index],
            ...updates,
          };
        }
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete announcement
    builder
      .addCase(deleteAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        state.announcements = state.announcements.filter(
          (a) => a.id !== action.payload
        );
        state.totalCount -= 1;
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Toggle status
    builder
      .addCase(toggleAnnouncementStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(toggleAnnouncementStatus.fulfilled, (state, action) => {
        const { id, is_active } = action.payload;
        const index = state.announcements.findIndex((a) => a.id === id);
        if (index !== -1) {
          state.announcements[index].is_active = is_active;
        }
      })
      .addCase(toggleAnnouncementStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setLimit, clearError } =
  announcementSlice.actions;

export default announcementSlice.reducer;
